import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  DatePicker,
  Empty,
  Form,
  Select,
  Table,
  Typography,
  type TableColumnProps,
} from '@arco-design/web-react';
import dayjs from 'dayjs';
import { adminWalletLedgerApi, customersApi } from '@/api';
import type { PaginatedResult } from '@/types/api';
import type { CustomerSummary } from '@/types/customer';
import type {
  WalletLedgerBizType,
  WalletLedgerListQuery,
  WalletLedgerRecord,
} from '@/types/walletLedger';
import { formatMoney } from '@/utils/money';

const { RangePicker } = DatePicker;

const bizTypeLabels: Record<WalletLedgerBizType, string> = {
  CARD_OPEN: '开卡扣款',
  CARD_OPEN_ROLLBACK: '开卡失败退回',
  CARD_REFILL: '卡充值扣款',
  CARD_REFILL_ROLLBACK: '充值失败退回',
  CARD_WITHDRAW: '卡提现入账',
  CARD_WRITEOFF: '销卡退回',
  ADMIN_CREDIT: '平台划入',
  ADMIN_DEBIT: '平台划出',
};

const bizTypeOptions: Array<{ label: string; value: WalletLedgerBizType | 'ALL' }> = [
  { label: '全部业务', value: 'ALL' },
  ...(
    Object.entries(bizTypeLabels) as Array<[WalletLedgerBizType, string]>
  ).map(([value, label]) => ({ label, value })),
];

export default function WalletLedgerView() {
  const [customers, setCustomers] = useState<CustomerSummary[]>([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [data, setData] = useState<PaginatedResult<WalletLedgerRecord> | null>(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState<Omit<WalletLedgerListQuery, 'customerId'> & { customerId?: string }>({
    page: 1,
    pageSize: 20,
  });

  const searchCustomers = useCallback((keyword?: string) => {
    setCustomersLoading(true);
    customersApi
      .list({ keyword: keyword || undefined, page: 1, pageSize: 50 })
      .then((res) => setCustomers(res.items))
      .catch(() => setCustomers([]))
      .finally(() => setCustomersLoading(false));
  }, []);

  useEffect(() => {
    searchCustomers();
  }, [searchCustomers]);

  const load = useCallback(() => {
    if (!query.customerId) {
      setData(null);
      return;
    }
    setLoading(true);
    adminWalletLedgerApi
      .list({ ...query, customerId: query.customerId })
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [query]);

  useEffect(() => {
    load();
  }, [load]);

  const customerOptions = useMemo(
    () =>
      customers.map((c) => ({
        value: c.id,
        label: `${c.username} · ${formatMoney(c.walletBalanceCents)}`,
      })),
    [customers],
  );

  const selectedCustomer = useMemo(
    () => customers.find((c) => c.id === query.customerId),
    [customers, query.customerId],
  );

  const columns: TableColumnProps<WalletLedgerRecord>[] = [
    {
      title: '记账时间',
      dataIndex: 'createdAt',
      width: 170,
      render: (v) => dayjs(v).format('YYYY-MM-DD HH:mm:ss'),
    },
    { title: '客户编号', dataIndex: 'customerNo', width: 120 },
    {
      title: '上次余额',
      dataIndex: 'balanceBeforeCents',
      width: 120,
      align: 'right',
      render: (v: number) => <span className="admin-mono">{formatMoney(v)}</span>,
    },
    {
      title: '本次发生额',
      dataIndex: 'amountCents',
      width: 130,
      align: 'right',
      render: (v: number) => (
        <span
          className="admin-mono"
          style={{ color: v >= 0 ? 'rgb(var(--green-6))' : undefined }}
        >
          {v >= 0 ? '+' : ''}
          {formatMoney(v)}
        </span>
      ),
    },
    {
      title: '本次余额',
      dataIndex: 'balanceAfterCents',
      width: 120,
      align: 'right',
      render: (v: number) => (
        <span className="admin-mono" style={{ fontWeight: 600 }}>
          {formatMoney(v)}
        </span>
      ),
    },
    {
      title: '扣费业务',
      dataIndex: 'bizType',
      width: 130,
      render: (v: WalletLedgerBizType) => bizTypeLabels[v] ?? v,
    },
    {
      title: '关联单号',
      dataIndex: 'bizRef',
      width: 200,
      ellipsis: true,
      render: (v) => v || '-',
    },
    {
      title: '备注',
      dataIndex: 'remark',
      ellipsis: true,
      render: (v) => v || '-',
    },
  ];

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <div>
          <div className="admin-page__title">资金流水</div>
          <div className="admin-page__subtitle">
            查看指定客户母账户钱包变动明细：上次余额、本次发生额、本次余额及扣费业务类型
          </div>
        </div>
      </div>

      <div className="admin-card">
        <Form
          layout="inline"
          style={{ marginBottom: 12 }}
          onSubmit={(values) =>
            setQuery((prev) => ({
              ...prev,
              ...values,
              page: 1,
            }))
          }
        >
          <Form.Item label="客户" required>
            <Select
              placeholder="搜索并选择客户"
              showSearch
              allowClear
              loading={customersLoading}
              style={{ width: 280 }}
              value={query.customerId}
              filterOption={false}
              onSearch={searchCustomers}
              onChange={(customerId) =>
                setQuery((prev) => ({
                  ...prev,
                  customerId: customerId || undefined,
                  page: 1,
                }))
              }
            >
              {customerOptions.map((o) => (
                <Select.Option key={o.value} value={o.value}>
                  {o.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="时间范围">
            <RangePicker
              onChange={(dateStrings) =>
                setQuery((prev) => ({
                  ...prev,
                  startDate: dateStrings?.[0] || undefined,
                  endDate: dateStrings?.[1] || undefined,
                  page: 1,
                }))
              }
            />
          </Form.Item>
          <Form.Item label="扣费业务">
            <Select
              placeholder="全部"
              allowClear
              style={{ width: 160 }}
              value={query.bizType ?? 'ALL'}
              onChange={(v) =>
                setQuery((prev) => ({
                  ...prev,
                  bizType: v === 'ALL' || !v ? undefined : (v as WalletLedgerBizType),
                  page: 1,
                }))
              }
            >
              {bizTypeOptions.map((o) => (
                <Select.Option key={o.value} value={o.value}>
                  {o.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" disabled={!query.customerId}>
              查询
            </Button>
          </Form.Item>
        </Form>

        {selectedCustomer && (
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            当前客户：{selectedCustomer.username} · 母账户余额{' '}
            {formatMoney(selectedCustomer.walletBalanceCents)}
          </Typography.Text>
        )}

        {!query.customerId ? (
          <Empty description="请先选择客户以查看资金流水" />
        ) : (
          <Table<WalletLedgerRecord>
            loading={loading}
            rowKey="id"
            data={data?.items ?? []}
            columns={columns}
            scroll={{ x: 1100 }}
            pagination={{
              current: query.page ?? 1,
              pageSize: query.pageSize ?? 20,
              total: data?.total ?? 0,
              showTotal: true,
              onChange: (page) => setQuery((prev) => ({ ...prev, page })),
            }}
          />
        )}
      </div>
    </div>
  );
}
